import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calendar, User } from "lucide-react";
import axios from "axios";
import SEO from "@/components/SEO";
import PostContent from "@/components/PostContent";
import { API } from "@/lib/api";

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPost = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog/${slug}`);
      setPost(response.data);
    } catch (err) {
      setError("Post not found");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="pt-20 min-h-screen flex flex-col items-center justify-center">
        <p className="text-muted-foreground mb-4">Article not found</p>
        <Link to="/blog">
          <Button variant="outline" className="rounded-full">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="blog-post-page">
      <SEO
        title={post.title}
        description={post.meta_description || post.excerpt}
        image={post.image_url}
        imageAlt={post.image_alt || post.title}
        keywords={[post.focus_keyword, ...(post.secondary_keywords || [])]}
        type="article"
        pathname={`/blog/${post.slug}`}
        publishedTime={post.created_at}
        author={post.author}
      />
      {/* Header */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-4xl mx-auto">
          <Link to="/blog">
            <Button variant="ghost" className="mb-6 -ml-4 text-muted-foreground hover:text-primary">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Button>
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-tight">
              {post.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-6 mt-6 text-muted-foreground">
              <span className="flex items-center gap-2">
                <User className="h-4 w-4" />
                {post.author}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {formatDate(post.created_at)}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-3xl mx-auto"
        >
          <article className="prose prose-lg max-w-none">
            <PostContent content={post.content} contentFont={post.content_font} />
          </article>

          {/* CTA */}
          <div className="mt-16 p-8 bg-secondary/50 rounded-2xl text-center">
            <h3 className="font-serif text-2xl font-semibold text-foreground mb-3">
              Ready to Start Your Healing Journey?
            </h3>
            <p className="text-muted-foreground mb-6">
              Have questions? Reach out through the contact form.
            </p>
            <Link to="/contact">
              <Button className="rounded-full px-8 py-6 text-lg bg-primary text-primary-foreground hover:bg-primary/90">
                Contact Laura
              </Button>
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default BlogPostPage;
